import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Send, Bot, User, Sparkles, Mic, MicOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check for browser speech recognition support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function PublicChat() {
  const { token } = useParams();
  const [chatData, setChatData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    initChat();
    // Check if speech recognition is supported
    setSpeechSupported(!!SpeechRecognition);
  }, [token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initChat = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/chat/${token}`);
      setChatData(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to initialize chat. Invalid or inactive agent.');
      setLoading(false);
    }
  };

  const toggleListening = () => {
    if (!SpeechRecognition) {
      toast.error('Speech recognition is not supported in your browser. Try Chrome or Edge.');
      return;
    }

    if (isListening) {
      // Stop listening
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
    } else {
      // Start listening
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        setInputMessage(transcript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone permissions.');
        } else if (event.error === 'no-speech') {
          toast.info('No speech detected. Try again.');
        } else {
          toast.error(`Speech recognition error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    }
  };

  const sendMessage = async (content) => {
    if (!content.trim()) return;

    // Stop listening if active
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }

    setSending(true);
    const userMsg = { role: 'user', content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/public/chat/${token}/message`,
        {
          content,
          session_id: sessionId,
        }
      );

      setSessionId(response.data.session_id);
      setMessages((prev) => [...prev, response.data.assistant_message]);
    } catch (error) {
      toast.error('Failed to send message');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const handleQuickButton = (promptText) => {
    sendMessage(promptText);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputMessage);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (!chatData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Card className="p-8 text-center max-w-md border-2 shadow-lg">
          <Bot className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Chat Not Found</h2>
          <p className="text-muted-foreground">This chat agent is not available or has been deactivated.</p>
        </Card>
      </div>
    );
  }

  const primaryColor = chatData.primary_color || '#6366f1';

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-8" data-testid="public-chat">
      <Card className="w-full max-w-2xl border-2 shadow-xl flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Header with branding */}
        <div
          className="border-b-2 px-6 py-4 flex-shrink-0"
          style={{ borderBottomColor: `${primaryColor}30` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {chatData.agent_logo ? (
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center ring-2 ring-offset-2 bg-white p-1"
                  style={{ ringColor: `${primaryColor}40` }}
                >
                  <img
                    src={chatData.agent_logo}
                    alt={chatData.agent_name}
                    className="max-w-full max-h-full object-contain"
                    data-testid="agent-logo"
                  />
                </div>
              ) : chatData.tenant_logo ? (
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white p-1">
                  <img
                    src={chatData.tenant_logo}
                    alt={chatData.tenant_name}
                    className="max-w-full max-h-full object-contain"
                    data-testid="tenant-logo"
                  />
                </div>
              ) : (
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <Bot className="w-7 h-7" style={{ color: primaryColor }} />
                </div>
              )}
              <div>
                <h1 className="text-xl font-heading font-bold text-foreground" data-testid="agent-name">
                  {chatData.agent_name}
                </h1>
                <p className="text-sm text-muted-foreground" data-testid="tenant-name">{chatData.tenant_name}</p>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="flex items-center space-x-1 px-3 py-1"
              style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="font-medium">AI Powered</span>
            </Badge>
          </div>
        </div>

        {/* Chat Messages Area */}
        <CardContent className="flex-1 overflow-y-auto p-6 space-y-4 min-h-[300px]" data-testid="messages-container">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div
                className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <Bot className="w-10 h-10" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-foreground mb-3">
                Welcome! How can I help you?
              </h3>
              <p className="text-muted-foreground">
                Type your message, use voice input, or tap a quick action below
              </p>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              data-testid={`message-${msg.role}-${idx}`}
            >
              <div className={`flex items-start space-x-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${
                    msg.role === 'user' ? 'bg-slate-200' : ''
                  }`}
                  style={msg.role === 'assistant' ? { backgroundColor: `${primaryColor}20` } : {}}
                >
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-slate-600" />
                  ) : chatData.agent_logo ? (
                    <img 
                      src={chatData.agent_logo} 
                      alt="" 
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <Bot className="w-4 h-4" style={{ color: primaryColor }} />
                  )}
                </div>
                <div
                  className={`px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'rounded-tr-sm text-white'
                      : 'rounded-tl-sm bg-slate-100 text-slate-900'
                  }`}
                  style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}
                >
                  {msg.role === 'assistant' ? (
                    <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-img:rounded-lg prose-img:max-h-48 prose-img:my-2 prose-a:text-blue-600 prose-a:underline prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-headings:font-semibold prose-h1:text-lg prose-h2:text-base prose-h3:text-sm">
                      <ReactMarkdown
                        components={{
                          img: ({ node, ...props }) => (
                            <img 
                              {...props} 
                              className="rounded-lg max-h-48 my-2 shadow-sm inline-block" 
                              loading="lazy"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ),
                          a: ({ node, children, ...props }) => (
                            <a 
                              {...props} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              {children} ↗
                            </a>
                          ),
                          p: ({ node, children }) => (
                            <p className="my-1.5">{children}</p>
                          ),
                          ul: ({ node, children }) => (
                            <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>
                          ),
                          ol: ({ node, children }) => (
                            <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>
                          ),
                          li: ({ node, children }) => (
                            <li className="my-0.5">{children}</li>
                          ),
                          strong: ({ node, children }) => (
                            <strong className="font-semibold text-gray-900">{children}</strong>
                          ),
                          h1: ({ node, children }) => (
                            <h1 className="text-lg font-bold mt-3 mb-1">{children}</h1>
                          ),
                          h2: ({ node, children }) => (
                            <h2 className="text-base font-semibold mt-2 mb-1">{children}</h2>
                          ),
                          h3: ({ node, children }) => (
                            <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>
                          ),
                          code: ({ node, inline, children }) => (
                            inline ? 
                              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code> :
                              <pre className="bg-gray-100 p-2 rounded my-2 overflow-x-auto"><code className="text-sm font-mono">{children}</code></pre>
                          )
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start animate-in fade-in">
              <div className="flex items-start space-x-2 max-w-[85%]">
                <div
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center overflow-hidden"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  {chatData.agent_logo ? (
                    <img 
                      src={chatData.agent_logo} 
                      alt="" 
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <Bot className="w-4 h-4" style={{ color: primaryColor }} />
                  )}
                </div>
                <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-slate-100">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input Area + Quick Buttons - Bottom Section */}
        <div className="border-t-2 p-4 flex-shrink-0 space-y-3" style={{ borderTopColor: `${primaryColor}20` }}>
          {/* Input Form */}
          <form onSubmit={handleSubmit} className="flex items-center space-x-2 sm:space-x-3">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={isListening ? "Listening..." : "Type your message..."}
              className={`flex-1 rounded-full h-12 px-6 border-2 transition-colors ${
                isListening ? 'border-red-400 bg-red-50' : 'focus:border-primary/50'
              }`}
              disabled={sending}
              data-testid="message-input"
            />
            
            {/* Microphone Button */}
            {speechSupported && (
              <Button
                type="button"
                size="icon"
                variant={isListening ? "destructive" : "outline"}
                className={`rounded-full w-12 h-12 transition-all ${
                  isListening 
                    ? 'animate-pulse bg-red-500 hover:bg-red-600 border-red-500' 
                    : 'hover:shadow-md border-2'
                }`}
                style={!isListening ? { borderColor: `${primaryColor}40`, color: primaryColor } : {}}
                onClick={toggleListening}
                disabled={sending}
                data-testid="mic-button"
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                {isListening ? (
                  <MicOff className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </Button>
            )}
            
            {/* Send Button */}
            <Button
              type="submit"
              size="icon"
              className="rounded-full w-12 h-12 shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl"
              style={{ backgroundColor: primaryColor }}
              disabled={sending || !inputMessage.trim()}
              data-testid="send-button"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>

          {/* Voice Input Indicator */}
          {isListening && (
            <div className="flex items-center justify-center space-x-2 text-sm text-red-600 animate-in fade-in">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span>Listening... Tap mic to stop</span>
            </div>
          )}

          {/* Quick Buttons - Below Input */}
          {chatData.quick_buttons.length > 0 && (
            <div className="pt-2" data-testid="quick-buttons">
              <div className="flex flex-wrap gap-2 justify-center">
                {chatData.quick_buttons.map((button) => (
                  <Button
                    key={button.button_id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickButton(button.prompt_text)}
                    className="rounded-full border-2 hover:shadow-md transition-all text-xs sm:text-sm"
                    style={{
                      borderColor: `${primaryColor}40`,
                      color: primaryColor,
                    }}
                    disabled={sending}
                    data-testid={`quick-button-${button.button_id}`}
                  >
                    {button.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
